/**
 * Free time in a working day, and the account's clock.
 *
 * The board's own slot finder (`apps/web/src/routes/schedule/lib/booking.ts`)
 * works in pixels and board types, so it stays in the web. This is the plain
 * version the assistant's "find a time" asks: given what is booked, where
 * are the gaps long enough for the job. Everything is in the account's
 * timezone, never the server's — Adelaide and UTC disagree about which day 8am
 * belongs to for nine and a half hours of every twenty-four. Decision 94.
 */

export interface Interval {
  start: Date
  end: Date
}

/** Milliseconds a zone is ahead of UTC at a given instant. */
export function zoneOffsetAt(at: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(at)

  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? '0')
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour') % 24,
    get('minute'),
    get('second'),
  )

  return asUtc - Math.floor(at.getTime() / 1000) * 1000
}

/**
 * Midnight at the start of `date` (`YYYY-MM-DD`) in the zone. Two passes,
 * because the offset depends on the instant, which matters on a DST boundary —
 * and Adelaide has one.
 */
export function zonedMidnight(date: string, timeZone: string): Date {
  const [year, month, day] = date.split('-').map(Number) as [number, number, number]
  let guess = Date.UTC(year, month - 1, day)
  for (let pass = 0; pass < 2; pass += 1) {
    guess = Date.UTC(year, month - 1, day) - zoneOffsetAt(new Date(guess), timeZone)
  }
  return new Date(guess)
}

/** A wall-clock time (`HH:MM`) on a day in the zone, as an instant. */
export function zonedTime(date: string, clock: string, timeZone: string): Date {
  const [hours, minutes] = clock.split(':').map(Number) as [number, number]
  const [year, month, day] = date.split('-').map(Number) as [number, number, number]
  const wall = Date.UTC(year, month - 1, day, hours, minutes)
  let guess = wall
  for (let pass = 0; pass < 2; pass += 1) {
    guess = wall - zoneOffsetAt(new Date(guess), timeZone)
  }
  return new Date(guess)
}

/** The calendar day (`YYYY-MM-DD`) an instant falls on in the zone. */
export function zonedDate(at: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(at)
}

/** The wall clock (`HH:MM`, 24-hour) an instant reads in the zone. */
export function zonedClock(at: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
    .format(at)
    .replace(/^24:/, '00:')
}

/** `date` plus `days`, still as `YYYY-MM-DD`. Calendar arithmetic, no zone. */
export function addCalendarDays(date: string, days: number): string {
  const [year, month, day] = date.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10)
}

const QUARTER_HOUR = 15 * 60_000

/**
 * The gaps in `[dayStart, dayEnd)` at least `minMinutes` long.
 *
 * Busy time may overlap, run past either edge, or arrive in any order. A gap
 * opens on the next quarter hour, because nobody books 10:07, and nothing
 * starts before `now` — a slot this morning that has already gone is not
 * free.
 */
export function freeWindows(input: {
  dayStart: Date
  dayEnd: Date
  busy: readonly Interval[]
  minMinutes: number
  now?: Date
}): Interval[] {
  const floor = Math.max(input.dayStart.getTime(), input.now?.getTime() ?? -Infinity)
  const end = input.dayEnd.getTime()
  const need = input.minMinutes * 60_000

  const busy = input.busy
    .map((interval) => ({ start: interval.start.getTime(), end: interval.end.getTime() }))
    .filter((interval) => interval.end > floor && interval.start < end)
    .sort((a, b) => a.start - b.start)

  const gaps: Interval[] = []
  let cursor = floor

  const open = (from: number, to: number) => {
    const start = Math.ceil(from / QUARTER_HOUR) * QUARTER_HOUR
    if (to - start >= need) gaps.push({ start: new Date(start), end: new Date(to) })
  }

  for (const interval of busy) {
    if (interval.start > cursor) open(cursor, interval.start)
    cursor = Math.max(cursor, interval.end)
  }
  if (end > cursor) open(cursor, end)

  return gaps
}
